import React, { useMemo, useState, forwardRef, useEffect } from 'react';

import TableContainer from '@hud/web-components/Container/TableContainer';
import { DefaultTable } from '@hud/web-components/Table/Table';
import { Search } from '@hud/web-components/Search/Search';
import { ItemCounter } from '@hud/web-components/ItemCounter/ItemCounter';
import { FlowByFunction, FunctionData } from '@hud/portal-dtos/webapp-function.dto';
import Pagination from '@hud/web-components/Pagination/Pagination';
import {
  PaginationState,
  SortingState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  FilterFn,
  Row,
} from '@tanstack/react-table';
import { HorizontalContainer } from '@hud/web-components/Container/HorizontalContainer';
import { DEFAULT_INNER_TABLE_PAGE_SIZE } from '../../../consts';
import renderMetric, {
  formatMetric,
} from '../../../components/metricCellRenderer/metricCellRenderer';
import { useGlobalQueryString } from '../../../hooks/useGlobalFilter';
import Skeleton from '@hud/web-components/Skeleton/Skeleton';
import { BreadcrumbItem } from '@hud/web-components/Breadcrumbs/Breadcrumbs';
import { formatDatetime, renderDatetime } from '@hud/web-components/Table/CellRenderers';
import renderFlowEntryPoint from '../../../components/FlowCellRenderer/FlowEntryPointCellRenderer';
import { entitiesCellRenderer } from '../../../components/entitiesCellRenderer/entitiesCellRenderer';
import renderFlowType from '../../../components/FlowCellRenderer/FlowTypeCellRenderer';
import useAnalytics from '../../../hooks/useAnalytics';
import useEnvironment from '../../../hooks/useEnvironment';
import { useFiltersForFunction } from '../../../hooks/useFiltersForFunction';

export type FlowsByFunctionTableProps = {
  function?: FunctionData;
  loading: boolean;
  back?: number;
  referralBreadcrumbs?: BreadcrumbItem[] | null;
  flows?: FlowByFunction[];
  directFlows?: FlowByFunction[];
  tags?: Record<string, string>;
};

const columnHelper = createColumnHelper<FlowByFunction[][0]>();

const FlowsByFunctionTable = forwardRef<HTMLDivElement, FlowsByFunctionTableProps>(
  ({ flows, directFlows, function: func, loading, referralBreadcrumbs, tags }, ref) => {
    const globalFilters = useGlobalQueryString();
    const functionFilters = useFiltersForFunction(undefined, undefined, tags);
    const analytics = useAnalytics();
    const { environment } = useEnvironment();
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState<SortingState>([
      {
        id: 'runs',
        desc: true,
      },
    ]);

    const [pagination, setPagination] = useState<PaginationState>({
      pageIndex: 0,
      pageSize: DEFAULT_INNER_TABLE_PAGE_SIZE,
    });

    const numGroupsPerTopic =
      flows === undefined
        ? {}
        : flows.reduce(
            (acc, flow) => {
              if (flow.type === 'KAFKA') {
                acc[flow.kafka_topic_name] = acc[flow.kafka_topic_name] ?? 0;
                acc[flow.kafka_topic_name]++;
              }
              return acc;
            },
            {} as Record<string, number>,
          );
    // get only the names that have more than one count from multiGroupTopics
    const multiGroupTopics = Object.keys(numGroupsPerTopic).filter(
      (key) => numGroupsPerTopic[key] > 1,
    );

    const columns = useMemo(() => {
      return [
        columnHelper.accessor('service', {
          header: 'Service',
          cell: entitiesCellRenderer('service'),
          size: 100,
          maxSize: 200,
          minSize: 110,
          sortingFn: (a, b) =>
            a.original.service.name.localeCompare(b.original.service.name),
          meta: {
            fitFormatter: (cell) => cell.getValue().name,
            disableAutoFit: true,
          },
        }),
        columnHelper.accessor('id', {
          header: 'Entry Point',
          cell: renderFlowEntryPoint(
            referralBreadcrumbs || [],
            multiGroupTopics,
            functionFilters,
          ),
          minSize: 220,
          size: 220,
          maxSize: 400,
          meta: {
            disableAutoFit: true,
          },
        }),
        columnHelper.accessor('type', {
          header: 'Type',
          cell: renderFlowType(directFlows || []),
          size: 100,
          minSize: 160,
          maxSize: 180,
          meta: {
            disableAutoFit: true,
          },
        }),
        columnHelper.accessor('runs', {
          header: 'Runs',
          cell: renderMetric('number'),
          size: 120,
          minSize: 80,
          maxSize: 250,
          sortingFn: (a, b) => a.original.runs - b.original.runs,
          meta: {
            fitFormatter: formatMetric('number'),
            rightAlign: true,
          },
        }),
        columnHelper.accessor('distribution', {
          header: 'Distribution',
          cell: renderMetric('percent'),
          size: 100,
          minSize: 100,
          maxSize: 250,
          meta: {
            disableAutoFit: true,
            rightAlign: true,
          },
        }),
        columnHelper.accessor('first_invoked_at', {
          header: 'First Invoked',
          cell: renderDatetime(true),
          minSize: 100,
          size: 100,
          maxSize: 250,
          meta: {
            disableAutoFit: true,
            fitFormatter: formatDatetime(true),
            rightAlign: true,
          },
        }),
        columnHelper.accessor('last_invoked_at', {
          header: 'Last Invoked',
          cell: renderDatetime(true),
          minSize: 100,
          size: 100,
          maxSize: 250,
          meta: {
            disableAutoFit: true,
            fitFormatter: formatDatetime(true),
            rightAlign: true,
          },
        }),
      ];
    }, [globalFilters, functionFilters]);

    const customGlobalFilterFunction: FilterFn<FlowByFunction[][0]> = (
      row: Row<FlowByFunction[][0]>,
      _columnId: string,
      searchTerms: string,
    ) => {
      const flattenedRow = {
        ...row.original,
        service_id: row.original?.service?.id,
        service_name: row.original?.service?.name,
      };
      return Object.values(flattenedRow).some((value) => {
        const cellValue = value?.toString().toLowerCase() || '';
        return cellValue.includes(searchTerms.toLowerCase().trim());
      });
    };

    const table = useReactTable({
      data: flows ?? [],
      columns: columns,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: setPagination,
      onGlobalFilterChange: setGlobalFilter,
      columnResizeMode: 'onChange',
      enableSortingRemoval: false,

      state: {
        pagination,
        sorting,
        globalFilter,
      },
      defaultColumn: {
        minSize: 50,
      },
      onSortingChange: setSorting,
      globalFilterFn: customGlobalFilterFunction,
    });

    useEffect(() => {
      if (table.getState().sorting.length === 0 || !func) return;
      analytics.capture('function_flows_sorted', {
        function_id: func.id,
        function_name: func.name,
        column_name: sorting[0]?.id ?? '',
        column_order: sorting[0]?.desc ? 'desc' : 'asc',
        environment: environment?.name,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sorting]);

    return (
      <TableContainer.Container ref={ref} title={'Exists in Flows'} id={`function-flows`}>
        <TableContainer.Header>
          <Search
            inline
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
          />
          {loading ? (
            <Skeleton width={100} />
          ) : flows ? (
            <ItemCounter
              total={flows.length}
              filtered={table.getPrePaginationRowModel().rows.length}
            />
          ) : null}
        </TableContainer.Header>
        <HorizontalContainer>
          <DefaultTable
            table={table}
            label={'Flows Table'}
            loading={loading}
            loadingRows={3}
          />
        </HorizontalContainer>
        <TableContainer.Footer>
          <Pagination
            pages={loading ? 0 : table.getPageCount()}
            currentPage={
              loading || table.getPageCount() === 0
                ? 0
                : table.getState().pagination.pageIndex + 1
            }
            onPageChange={(page) => table.setPageIndex(page - 1)}
            loading={loading}
          />
        </TableContainer.Footer>
      </TableContainer.Container>
    );
  },
);

export default FlowsByFunctionTable;
